import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./Login.css";
import API_BASE_URL from "../config";
import { getTrustedDeviceToken, setAuthSession } from "../utils/authStorage";

export default function VerifyLogin() {
  const navigate = useNavigate();
  const location = useLocation();

  const [pending2FA, setPending2FA] = useState(null);
  const [loginLogoUrl, setLoginLogoUrl] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function loadLoginLogo() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/platform-settings`);
        const data = await res.json();

        if (isMounted) {
          setLoginLogoUrl(data?.login_logo_url || "");
        }
      } catch (err) {
        console.error("Failed to load login logo:", err);
      }
    }

    loadLoginLogo();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    try {
      const stateData =
        location.state && location.state.challengeId
          ? location.state
          : null;

      if (stateData) {
        setPending2FA(stateData);
        sessionStorage.setItem("pending_2fa", JSON.stringify(stateData));
        return;
      }

      const stored = sessionStorage.getItem("pending_2fa");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.challengeId) {
          setPending2FA(parsed);
          return;
        }
      }

      navigate("/login", { replace: true });
    } catch (err) {
      console.error("Failed to load pending 2FA session:", err);
      navigate("/login", { replace: true });
    }
  }, [location.state, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();

    if (!pending2FA?.challengeId) {
      setErrorMsg("Your verification session is missing. Please log in again.");
      navigate("/login", { replace: true });
      return;
    }

    if (!code.trim()) {
      setErrorMsg("Enter the verification code.");
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      const trustedDeviceToken = getTrustedDeviceToken();

      const res = await fetch(`${API_BASE_URL}/api/auth/verify-2fa`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(trustedDeviceToken
            ? { "x-trusted-device-token": trustedDeviceToken }
            : {})
        },
        body: JSON.stringify({
          challengeId: Number(pending2FA.challengeId),
          code: code.trim(),
          trustedDeviceToken: trustedDeviceToken || null
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Invalid verification code");
      }

      const storedRedirectPath = sessionStorage.getItem("redirect_after_login");
      const platformRoleKey =
        data.user?.platformRole?.key_name ||
        data.user?.platform_role?.key_name ||
        data.user?.platform_role_key ||
        data.user?.platformRoleKey ||
        "";

      const redirectPath =
        storedRedirectPath ||
        (platformRoleKey === "platform_support" ? "/support-admin" : "/dashboard");

      setAuthSession({
        token: data.token,
        user: data.user || {},
        demoSession: null,
        trustedDeviceToken: data.trustedDeviceToken || trustedDeviceToken || null
      });

      sessionStorage.removeItem("pending_2fa");
      sessionStorage.removeItem("redirect_after_login");

      navigate(redirectPath, { replace: true });
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!pending2FA?.challengeId) {
      setErrorMsg("Your verification session is missing. Please log in again.");
      navigate("/login", { replace: true });
      return;
    }

    try {
      setResending(true);
      setErrorMsg("");

      const res = await fetch(`${API_BASE_URL}/api/auth/resend-2fa`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          challengeId: Number(pending2FA.challengeId)
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Unable to resend code");
      }

      const updatedPending = {
        ...pending2FA,
        challengeId: data.challengeId || pending2FA.challengeId,
        emailMasked: data.emailMasked || pending2FA.emailMasked
      };

      setPending2FA(updatedPending);
      sessionStorage.setItem("pending_2fa", JSON.stringify(updatedPending));
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-background-orb auth-background-orb-one" />
      <div className="auth-background-orb auth-background-orb-two" />

      <div className="auth-card auth-card-verify">
        <div className="auth-brand auth-brand-centered">
          <div className="auth-brand-logo auth-brand-logo-image" aria-label="Agent Book CRM logo">
            <img
              src={loginLogoUrl || "/logos/your-logo.png"}
              alt="Agent Book CRM Logo"
            />
          </div>

          <div className="auth-brand-copy">
            <p className="auth-brand-kicker">Secure Verification</p>
            <h1 className="auth-brand-title">Verify Login</h1>
            <p className="auth-brand-subtitle">
              Enter the 6-digit verification code sent to{" "}
              <strong>{pending2FA?.emailMasked || "your email"}</strong>.
            </p>
          </div>
        </div>

        <form className="auth-form" onSubmit={handleVerify}>
          <div className="auth-form-section">
            <div className="auth-field">
              <label>Verification Code</label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="Enter 6-digit code"
                value={code}
                autoFocus
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setCode(val);
                }}
                maxLength={6}
                required
              />
            </div>
          </div>

          {errorMsg && <div className="auth-error-banner">{errorMsg}</div>}

          <button
            type="submit"
            className="auth-primary-btn"
            disabled={loading || !pending2FA?.challengeId}
          >
            {loading ? "Verifying..." : "Verify & Login"}
          </button>

          <div className="auth-secondary-actions">
            <button
              type="button"
              className="auth-secondary-btn"
              onClick={handleResend}
              disabled={resending || !pending2FA?.challengeId}
            >
              {resending ? "Sending..." : "Resend Code"}
            </button>

            <button
              type="button"
              className="auth-ghost-btn"
              onClick={() => {
                sessionStorage.removeItem("pending_2fa");
                navigate("/login", { replace: true });
              }}
            >
              Back to Login
            </button>
          </div>

          <div className="auth-footer-note">
            Verification codes expire quickly for your account security.
          </div>
        </form>
      </div>
    </div>
  );
}